class VspotsVerifiedReviews extends HTMLElement {
  connectedCallback() {
    this.claimForm = this.querySelector('[data-review-claim-form]');
    this.reviewForm = this.querySelector('[data-review-form]');
    this.claimPanel = this.querySelector('[data-review-claim-panel]');
    this.reviewPanel = this.querySelector('[data-review-panel]');
    this.successPanel = this.querySelector('[data-review-success]');
    this.status = this.querySelector('[data-review-status]');
    this.context = this.querySelector('[data-review-context]');
    this.invitationSection = this.closest('[data-review-invitation-section]');
    this.ratingInputs = [...this.querySelectorAll('input[name="rating"]')];

    if (!this.claimForm || !this.reviewForm || !this.status) return;

    this.claimForm.addEventListener('submit', this.handleClaim);
    this.reviewForm.addEventListener('submit', this.handleSubmit);
    this.ratingInputs.forEach((input) => input.addEventListener('change', this.handleRating));
    void this.activateInvitationFromUrl();
  }

  disconnectedCallback() {
    this.claimForm?.removeEventListener('submit', this.handleClaim);
    this.reviewForm?.removeEventListener('submit', this.handleSubmit);
    this.ratingInputs?.forEach((input) => input.removeEventListener('change', this.handleRating));
  }

  handleRating = (event) => {
    const value = Number(event.currentTarget.value);
    this.querySelectorAll('[data-rating-value]').forEach((star) => {
      star.dataset.active = Number(star.dataset.ratingValue) <= value ? 'true' : 'false';
    });
  };

  handleClaim = async (event) => {
    event.preventDefault();
    const form = new FormData(this.claimForm);
    const submit = this.claimForm.querySelector('[type="submit"]');
    this.setBusy(submit, true);
    this.setStatus(this.dataset.claimLoading, 'loading');

    try {
      const response = await this.request('/v1/reviews/claim', {
        body: JSON.stringify({
          email: String(form.get('email') || '').trim().toLowerCase(),
          productId: this.dataset.productId,
        }),
        headers: { 'Content-Type': 'application/json' },
        method: 'POST',
      });
      const payload = await response.json();
      if (!response.ok || !payload.invitation?.token) throw new Error(payload.error || 'claim_failed');

      this.reviewClaimedByEmail = true;
      this.showReviewPanel(payload.invitation, payload.invitation.token);
      this.setStatus('', 'idle');
      this.reviewPanel.querySelector('input[name="rating"]')?.focus();
    } catch (error) {
      this.setStatus(this.messageForClaimError(error), 'error');
    } finally {
      this.setBusy(submit, false);
    }
  };

  handleSubmit = async (event) => {
    event.preventDefault();
    const form = new FormData(this.reviewForm);
    const submit = this.reviewForm.querySelector('[type="submit"]');
    const selectedRating = this.reviewForm.querySelector('input[name="rating"]:checked');
    const consent = this.reviewForm.querySelector('input[name="termsAccepted"]');
    const rating = Number(selectedRating?.value);

    if (!Number.isInteger(rating) || rating < 1 || rating > 5) {
      this.setStatus(this.dataset.ratingRequired, 'error');
      return;
    }
    if (!this.reviewToken) {
      this.setStatus(this.dataset.claimError, 'error');
      return;
    }

    this.setBusy(submit, true);
    this.setStatus(this.dataset.submitLoading, 'loading');

    try {
      const body = String(form.get('body') || '').trim();
      const displayName = String(form.get('displayName') || '').trim();
      const title = String(form.get('title') || '').trim();
      const submission = {
        ...(body ? { body } : {}),
        ...(displayName ? { displayName } : {}),
        rating,
        termsAccepted: consent?.checked === true,
        ...(title ? { title } : {}),
      };
      let payload;
      try {
        payload = await this.submitReview(submission);
      } catch (error) {
        if (this.isInvalidInvitation(error) && this.reviewClaimedByEmail) await this.refreshInvitation();
        else if (!this.isRetryableSubmission(error)) throw error;
        payload = await this.submitReview(submission);
      }

      this.reviewToken = undefined;
      this.reviewPanel.hidden = true;
      this.successPanel.hidden = false;
      this.setStatus('', 'idle');
      this.successPanel.focus();
    } catch (error) {
      this.setStatus(this.messageForSubmitError(error), 'error');
    } finally {
      this.setBusy(submit, false);
    }
  };

  async request(path, options) {
    const apiBaseUrl = this.dataset.apiBaseUrl.replace(/\/$/u, '');
    return fetch(`${apiBaseUrl}${path}`, { ...options, credentials: 'omit', mode: 'cors' });
  }

  async activateInvitationFromUrl() {
    const parameters = new URLSearchParams(window.location.hash.replace(/^#/u, ''));
    const token = parameters.get('review_token');
    if (!token) return;

    window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
    if (this.invitationSection) this.invitationSection.hidden = false;
    const rating = Number(parameters.get('rating'));
    if (!/^[A-Za-z0-9_-]{43}$/u.test(token)) {
      this.setStatus(this.dataset.claimTechnicalError, 'error');
      return;
    }

    this.setStatus(this.dataset.claimLoading, 'loading');
    try {
      const response = await this.request('/v1/reviews/invitation', {
        headers: { Authorization: `Review ${token}` },
        method: 'GET',
      });
      const payload = await response.json();
      if (!response.ok || !payload.invitation) throw new Error(payload.error || 'invitation_failed');
      if (payload.invitation.externalProductId !== this.dataset.productId) throw new Error('invitation_product_mismatch');

      this.reviewClaimedByEmail = false;
      this.showReviewPanel(payload.invitation, token);
      if (Number.isInteger(rating) && rating >= 1 && rating <= 5) {
        const input = this.reviewForm.querySelector(`input[name="rating"][value="${rating}"]`);
        if (input) {
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
      this.setStatus('', 'idle');
      this.scrollIntoView({ behavior: 'smooth', block: 'start' });
      this.reviewPanel.querySelector('input[name="rating"]:checked, input[name="rating"]')?.focus();
    } catch (error) {
      this.setStatus(this.messageForClaimError(error), 'error');
    }
  }

  showReviewPanel(invitation, token) {
    this.reviewToken = token;
    this.claimPanel.hidden = true;
    this.reviewPanel.hidden = false;
    if (this.context) {
      this.context.textContent = this.dataset.contextTemplate
        .replace('__DEAL__', invitation.dealTitle || '')
        .replace('__PARTNER__', invitation.partnerName || '');
    }
  }

  async refreshInvitation() {
    const email = String(new FormData(this.claimForm).get('email') || '')
      .trim()
      .toLowerCase();
    const response = await this.request('/v1/reviews/claim', {
      body: JSON.stringify({ email, productId: this.dataset.productId }),
      headers: { 'Content-Type': 'application/json' },
      method: 'POST',
    });
    const payload = await response.json();
    if (!response.ok || !payload.invitation?.token) throw new Error(payload.error || 'claim_failed');
    this.reviewToken = payload.invitation.token;
  }

  async submitReview(submission) {
    const response = await this.request('/v1/reviews', {
      body: JSON.stringify(submission),
      headers: {
        Authorization: `Review ${this.reviewToken}`,
        'Content-Type': 'application/json',
      },
      method: 'POST',
    });
    const payload = await response.json();
    if (!response.ok || !payload.review?.id) throw new Error(payload.error || 'submit_failed');
    return payload;
  }

  isInvalidInvitation(error) {
    return ['review_invitation_expired', 'review_invitation_invalid', 'review_invitation_revoked'].includes(error.message);
  }

  isRetryableSubmission(error) {
    return error instanceof TypeError || error.message === 'review_submission_failed';
  }

  messageForClaimError(error) {
    if (error.message === 'review_already_submitted') return this.dataset.alreadySubmitted;
    if (error.message === 'review_eligibility_not_found') return this.dataset.claimError;
    return this.dataset.claimTechnicalError;
  }

  messageForSubmitError(error) {
    return error.message === 'review_already_submitted' ? this.dataset.alreadySubmitted : this.dataset.submitError;
  }

  setBusy(button, busy) {
    if (!button) return;
    button.disabled = busy;
    button.setAttribute('aria-busy', String(busy));
  }

  setStatus(message, state) {
    this.status.textContent = message || '';
    this.status.dataset.state = state;
    this.status.hidden = !message;
  }
}

if (!customElements.get('vspots-verified-reviews')) {
  customElements.define('vspots-verified-reviews', VspotsVerifiedReviews);
}
